import { baseApi } from '@/api/baseApi';

import type {
  AuthTokenResponse,
  GetUserDetailsByEmailIdsRequest,
  GoogleCallbackRequest,
  GooglePPTTokenRequest,
  GoogleSigninResponse,
  IsUserAllowedRequest,
  IsUserAllowedResponse,
  LogoutResponse,
  OtpResponse,
  SendOtpRequest,
  UserDetailsResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  VerifyTokenResponse,
} from './types';

const AUTH_V1_PLAYGROUND = '/auth/v1/playground';

const authApi = baseApi.injectEndpoints({
  endpoints: build => ({
    // POST /auth/v1/playground/sendOtp
    sendOtp: build.mutation<OtpResponse, SendOtpRequest>({
      query: body => ({
        url: `${AUTH_V1_PLAYGROUND}/sendOtp`,
        method: 'POST',
        body,
      }),
    }),

    // POST /auth/v1/playground/verifyOtp
    verifyOtp: build.mutation<VerifyOtpResponse, VerifyOtpRequest>({
      query: body => ({
        url: `${AUTH_V1_PLAYGROUND}/verifyOtp`,
        method: 'POST',
        body,
      }),
      transformResponse: (res: any) => res.data,
    }),

    // GET /auth/v1/playground/logout
    logout: build.mutation<LogoutResponse, void>({
      query: () => ({
        url: `${AUTH_V1_PLAYGROUND}/logout`,
        method: 'GET',
      }),
    }),

    // GET /auth/v1/playground/verifytoken
    verifyToken: build.query<VerifyTokenResponse, void>({
      query: () => `${AUTH_V1_PLAYGROUND}/verifytoken`,
    }),

    // GET /auth/v1/playground/googleSignin
    getGoogleSigninUrl: build.query<GoogleSigninResponse, void>({
      query: () => ({
        url: `${AUTH_V1_PLAYGROUND}/googleSignin`,
        params: { showSpinner: true },
      }),
    }),

    // GET /auth/v1/playground/google/callback
    googleCallback: build.query<AuthTokenResponse, GoogleCallbackRequest>({
      query: ({ code }) => ({
        url: `${AUTH_V1_PLAYGROUND}/google/callback`,
        params: { code, showSpinner: true },
      }),
      transformResponse: (res: any) => res.data ?? res,
    }),

    // GET /auth/v1/playground/googlePPTSignin
    getGooglePPTSigninUrl: build.query<GoogleSigninResponse, void>({
      query: () => `${AUTH_V1_PLAYGROUND}/googlePPTSignin`,
    }),

    // POST /auth/v1/playground/googlePPTToken
    googlePPTToken: build.mutation<AuthTokenResponse, GooglePPTTokenRequest>({
      query: body => ({
        url: `${AUTH_V1_PLAYGROUND}/googlePPTToken`,
        method: 'POST',
        body,
      }),
    }),

    // POST /auth/v1/playground/isUserAllowed
    isUserAllowed: build.query<IsUserAllowedResponse, IsUserAllowedRequest>({
      query: body => ({
        url: `${AUTH_V1_PLAYGROUND}/isUserAllowed`,
        method: 'POST',
        body,
      }),
    }),

    // POST /auth/v1/playground/getUserDetailsByEmailIds
    getUserDetailsByEmailIds: build.query<UserDetailsResponse, GetUserDetailsByEmailIdsRequest>({
      query: body => ({
        url: `${AUTH_V1_PLAYGROUND}/getUserDetailsByEmailIds`,
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useSendOtpMutation,
  useVerifyOtpMutation,
  useLogoutMutation,
  useVerifyTokenQuery,
  useLazyVerifyTokenQuery,
  useGetGoogleSigninUrlQuery,
  useLazyGetGoogleSigninUrlQuery,
  useGoogleCallbackQuery,
  useLazyGoogleCallbackQuery,
  useGetGooglePPTSigninUrlQuery,
  useLazyGetGooglePPTSigninUrlQuery,
  useGooglePPTTokenMutation,
  useIsUserAllowedQuery,
  useLazyIsUserAllowedQuery,
  useGetUserDetailsByEmailIdsQuery,
  useLazyGetUserDetailsByEmailIdsQuery,
} = authApi;
